import React, {useEffect, useRef, useState} from 'react';
import moment from 'moment';
import i18n from "i18next";
import {useTranslation} from "react-i18next";
import DynamicList from "../DynamicList/DynamicList";
import LabeledDatePicker from "../../Custom UI/LabeledInputs/LabeledDatePicker/LabeledDatePicker";
import LabeledDelayedInput from "../../Custom UI/LabeledInputs/LabeledDelayedInput/LabeledDelayedInput";
import "../../Pages/AdminHistory/AdminHistory.scss";
import {roleForCompany} from "../../Helpers/CompanyHelpers";

const defaultInitialFromDate = moment().subtract(1, 'year');
const defaultInitialToDate = moment();
const paperTrailRoles = ['admin', 'admin_level_1', 'admin_level_4', 'admin_level_6', 'admin_level_7', 'admin_level_9',
  'admin_level_11', 'physician', 'admin_level_66'];

const PaperTrailVersionsList = ({company, userId, id = null, type = 'user', getAdminHistories, studyClosed, showPatientSearch = true,
                                  initialIdFilter = null, initialUserIdFilter = null, initialFromDate = defaultInitialFromDate,
                                  initialToDate = defaultInitialToDate}) => {
  const hasItemFilter = id !== null && id !== undefined;
  const resolvedUserId = userId;
  const itemType = hasItemFilter ? type : null;
  const itemId = hasItemFilter ? id : null;
  const resolvedInitialIdFilter = initialIdFilter !== null && initialIdFilter !== undefined
    ? initialIdFilter
    : initialUserIdFilter;
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [idFilter, setIdFilter] = useState(resolvedInitialIdFilter);
  const [shouldReload, setShouldReload] = useState(false);
  const [historiesToShowChangesFor, setHistoriesToShowChangesFor] = useState([]);
  const previousInitialFromDate = useRef(initialFromDate);
  const {t} = useTranslation();

  useEffect(() => {
    setIdFilter(resolvedInitialIdFilter);
  }, [resolvedInitialIdFilter]);

  useEffect(() => {
    if(previousInitialFromDate.current !== initialFromDate){
      setFromDate(initialFromDate);
      setShouldReload(true);
    }
    previousInitialFromDate.current = initialFromDate;
  }, [initialFromDate]);

  if(!paperTrailRoles.includes(roleForCompany(company))){
    return (
      <div className="audit-history-container">
        <div className="audit-history-list-container">
          <div className="no-information-text">
            {i18n.t('PAPERTRAIL_ROLE_NOT_SUPPORTED')}
          </div>
        </div>
      </div>
    );
  }

  const renderUpdatingAction = (aEventType, aHistory) => {
    let returnValue = '';

    if(aEventType && aHistory){
      const translationKey = aEventType === 'destroy' ? 'DESTROYED' : aEventType.toUpperCase() + 'D';
      returnValue = i18n.t(translationKey) + ' ' + aHistory.item_type + ' (ID: ' + aHistory.id + ')';
    }
    return returnValue;
  };

  const renderUpdatedBy = (aValue) => {
    let returnValue = '';

    if(!aValue){
      return returnValue;
    }

    if(aValue){
      if(aValue.first_name != null){
        returnValue = aValue.first_name;
      }
      if(aValue.last_name != null){
        returnValue += ' ' + aValue.last_name;
      }
    }

    if(studyClosed){
      returnValue = 'ID: ' + aValue.id;
    }
    else{
      returnValue += ' - ID: ' + aValue.id;
    }
    return returnValue;
  };

  const toggleHistoryChanges = (aHistoryId) => {
    setHistoriesToShowChangesFor((previousHistories) => {
      if(previousHistories.includes(aHistoryId)){
        return previousHistories.filter((aValue) => aValue !== aHistoryId);
      }
      return [...previousHistories, aHistoryId];
    });
  };

  const renderChanges = (aHistory) => {
    let historiesArray = [];

    if(aHistory && aHistory.change_list){
      const maxLength = 111;
      const changeList = aHistory.change_list;

      for(let change of changeList){
        let newValue = '';
        let oldValue = '';

        if(change.new_value){
          newValue = change.new_value.toString();

          if(newValue.length > maxLength){
            newValue = newValue.substring(0, maxLength - 3) + '...';
          }
        }
        if(change.old_value){
          oldValue = change.old_value.toString();

          if(oldValue.length > maxLength){
            oldValue = oldValue.substring(0, maxLength - 3) + '...';
          }
        }
        historiesArray.push({key:change.key, old_value:oldValue, new_value:newValue});
      }
    }

    const shortenedHistoriesArray = historiesArray.slice(0, 2);
    const shouldShowSwitch = shortenedHistoriesArray.length < historiesArray.length;
    const shouldShowAllChanges = aHistory && Array.isArray(historiesToShowChangesFor) && historiesToShowChangesFor.includes(aHistory.id);
    const historiesToRender = shouldShowAllChanges ? historiesArray : shortenedHistoriesArray;

    return (
      <div>
        <div>
          {historiesToRender.map((change, index) => {
            return (
              <div key={index}
                   style={{fontSize:12, width:'100%', wordBreak:'break-all', wordWrap:'break-word'}}>
                {t('CHANGED_KEY', {change})}
              </div>
            );
          })}

          {shouldShowSwitch ?
            <div style={{fontSize:12, fontWeight:400}}
                 onClick={() => toggleHistoryChanges(aHistory.id)}>
              {shouldShowAllChanges ? t('SHOW_LESS') : t('SHOW_MORE')}
            </div>
            :
            null
          }
        </div>
      </div>
    );
  };

  const columnsArray = [
    {key:'action', columnWeight:2, columnNameKey:'ACTION', propertiesArray:['event_type'], valueFormatter:(aEventType, aHistory) => renderUpdatingAction(aEventType, aHistory)},
    {key:'updated_at', columnWeight:1, columnNameKey:'UPDATED_AT', propertiesArray:['edited_at'], dateFormat:'YYYY-MM-DD, h:mm:ss a'}
  ];

  if(!resolvedUserId){
    columnsArray.push({key:'updated_by', columnWeight:1, columnNameKey:'UPDATED_BY', propertiesArray:['edited_by'], valueFormatter:(aValue) => renderUpdatedBy(aValue)});
  }
  columnsArray.push({key:'changes', columnWeight:3, columnNameKey:'CHANGES', templateCell:(aHistory) => renderChanges(aHistory)});

  if(!getAdminHistories){
    return (
      <div className="audit-history-container">
        <div className="audit-history-list-container">
          <div style={{padding:'16px', border:'1px dashed #d9d9d9', borderRadius:'6px', color:'#777'}}>
            {i18n.t('NO_INFORMATION_AVAILABLE')}
          </div>
        </div>
      </div>
    );
  }

  const emptyListMessage = i18n.t('NO_PAPERTRAIL_CHANGES', {
    aItemType:itemType || type,
    aItemId:itemId || resolvedUserId || idFilter,
    aFromDate:fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
    aUpToDate:toDate ? moment(toDate).format('YYYY-MM-DD') : ''
  });

  return (
    <div className="audit-history-container">
      <div className="audit-history-list-container"
           id='admin-histories-list'>
        <DynamicList id="admin-history-list"
                     didFetch={() => setLoading(false)}
                     didReload={() => setShouldReload(false)}
                     showTotal={false}
                     headerView={
                      <div className={showPatientSearch ? "admin-history-header-container": ""}>
                        <div className="audit-history-date-range-container">
                          <div className="audit-history-search-input-container">
                            <LabeledDatePicker className="audit-history-search-input"
                                                label={i18n.t('FROM_DATE')}
                                                value={fromDate}
                                                loading={loading}
                                                maxDate={moment()}
                                                disabled={loading}
                                                isClearable={true}
                                                handleSave={(aDate) => {
                                                  setLoading(true);
                                                  setFromDate(aDate);
                                                  setShouldReload(true);
                                                }}
                            />
                          </div>

                          <div className="audit-history-search-input-container">
                            <LabeledDatePicker className="audit-history-search-input"
                                                label={i18n.t('TO_DATE')}
                                                value={toDate}
                                                loading={loading}
                                                maxDate={moment()}
                                                disabled={loading}
                                                isClearable={true}
                                                handleSave={(aDate) => {
                                                  setLoading(true);
                                                  setToDate(aDate);
                                                  setShouldReload(true);
                                                }}
                            />
                          </div>
                        </div>

                        {showPatientSearch ?
                          <div className="admin-history-search-by-specific-patient-input-container">
                            <LabeledDelayedInput className="admin-history-search-by-specific-patient-input"
                                                 label={i18n.t('SEARCH_BY_SPECIFIC_PATIENT')}
                                                 value={idFilter}
                                                 debounceTimeout={300}
                                                 minLength={1}
                                                 placeholder={i18n.t('SEARCH_PATIENT')}
                                                 clearButtonClicked={() => {
                                                   setIdFilter('');
                                                   setShouldReload(true);
                                                 }}
                                                 handleSave={(aEvent) => {
                                                   setShouldReload(true);
                                                   setIdFilter(aEvent.target.value);
                                                 }}
                            />
                          </div>
                          : null
                        }
                      </div>
                     }
                     columnsArray={columnsArray}
                     fetchObjects={(aLimit, aOffset) => getAdminHistories(resolvedUserId, toDate, fromDate, aLimit, aOffset, idFilter, itemType, itemId)}
                     shouldReload={shouldReload}
                     emptyListMessage={emptyListMessage}
                     showSearchBar={false}
                     explanationKey={'AUDIT_LOG_TEXT'}
                     minColumnWidth={120}
                     responseTotalKeysArray={['data', 'data', 'admin_history_page', 'total']}
                     responseObjectKeysArray={['data', 'data', 'admin_history_page', 'admin_histories']}
        />
      </div>
    </div>
  );
};

export default PaperTrailVersionsList;
